import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import { useLogoTheme } from '../context/LogoThemeContext';
import { fetchItemBySlug } from '../lib/cms';

export default function ForbundetFraHjertetPage() {
  const { headerBgColor } = useLogoTheme();
  const [pageData, setPageData] = useState(null);

  useEffect(() => {
    async function loadData() {
      const data = await fetchItemBySlug('forlob_sider', 'forbundet-fra-hjertet');
      if (data) setPageData(data);
    }
    loadData();
  }, []);

  const heroBadge = pageData?.hero_badge || 'Gruppeforløb for kvinder';
  const heroOverskrift = pageData?.hero_overskrift || 'FORBUNDET FRA HJERTET';
  const heroUndertitel = pageData?.hero_undertitel || 'Et trygt og kærligt rum for kvinder, der længes efter at komme hjem til sig selv';
  const badge1 = pageData?.badge_linje_1 || '5 mødegange · hver anden uge · 2,5 timer';
  const badge2 = pageData?.badge_linje_2 || '6 kvinder · 3.500 kr.';
  const prisTekst = pageData?.pris || '3.500 kr.';
  const rammerOverskrift = pageData?.rammer_overskrift || 'Praktisk';
  const rammerPunkter = pageData?.rammer_punkter;
  const ctaTekst = pageData?.cta_tekst || 'Send en personlig besked';

  return (
    <>
      <SEO
        title="Forbundet fra Hjertet - Lenea Evolinee"
        description="Et trygt og kærligt rum for kvinder, der længes efter at komme hjem til sig selv. Intimt gruppeforløb for kvinder hos Lenea Evolinee."
      />

      {/* Hero Header */}
      <section style={{ backgroundColor: headerBgColor }} className="pt-36 pb-20 transition-colors duration-300">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-4">
          <span className="text-xs uppercase tracking-widest text-[#98866C] font-semibold block">
            {heroBadge}
          </span>
          <h1 className="font-serif text-4xl sm:text-5xl font-normal text-[#2E2A27]">
            {heroOverskrift}
          </h1>
          <p className="text-lg sm:text-xl text-[#5C524A] leading-relaxed max-w-2xl mx-auto font-sans font-medium">
            {heroUndertitel}
          </p>
          <div className="pt-4 flex flex-wrap justify-center gap-3">
            <span className="inline-block px-4 py-1.5 rounded-full bg-[#FAF7F2] border border-[#D8CDBF] text-xs font-semibold text-[#2E2A27] tracking-wider uppercase">
              {badge1}
            </span>
            <span className="inline-block px-4 py-1.5 rounded-full bg-[#F3E6E2] border border-[#D8ADA0]/40 text-xs font-semibold text-[#864B3D] tracking-wider uppercase">
              {badge2}
            </span>
          </div>
        </div>
      </section>

      {/* Sektion 1: Min egen rejse & Kaldet */}
      <section className="py-20 bg-white border-y border-[#E8DFD5]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 space-y-8">
          <div className="space-y-4">
            <h2 className="font-serif text-3xl sm:text-4xl font-normal text-[#2E2A27]">
              Min egen rejse & Kaldet
            </h2>
            <div className="w-16 h-0.5 bg-[#98866C]/60" />
          </div>

          {pageData?.indhold_html ? (
            <div
              className="space-y-6 text-base sm:text-lg text-[#5C524A] leading-relaxed font-sans cms-richtext"
              dangerouslySetInnerHTML={{ __html: pageData.indhold_html }}
            />
          ) : (
            <div className="space-y-6 text-base sm:text-lg text-[#5C524A] leading-relaxed font-sans">
              <p>
                Der har været en længere, indadvendt og transformerende periode i mit eget liv, hvor jeg igen og igen er blevet mindet om, hvor vigtigt det er at kunne være sig selv – hel fra hjertet.
              </p>
              <p>
                Det har krævet, at jeg har kigget ind i mit indre mørke og så på nogle gamle mønstre, overbevisninger og historier jeg stadigvæk havde om mig selv. Det var smertefuldt, hårdt og skamfuldt, men nødvendigt, for at jeg kunne komme op til overfladen igen og mærke troen og kærligheden til mig selv.
              </p>

              <div className="my-8 p-6 sm:p-8 bg-[#FAF7F2] rounded-sm border-l-4 border-l-[#98866C] border-y border-r border-[#E8DFD5] space-y-2 text-[#2E2A27]">
                <p>At kunne lægge det fra sig, som ikke længere føles sandt.</p>
                <p>At turde mærke efter.</p>
                <p>At slippe behovet for at være den, andre forventer, jeg skal være.</p>
              </div>

              <p>
                At opleve, det dødede jeg ikke af, selvom det føltes sådan da jeg stod i det.
              </p>
              <p>
                Og netop dér opstod et stærkt kald i mig.
              </p>
              <p className="font-medium text-[#2E2A27]">
                Et kald til at skabe et trygt og fortroligt rum for kvinder, som mærker, at noget i dem længes efter forandring.
              </p>
            </div>
          )}
        </div>
      </section>

      {/* Sektion 2: Måske mærker du... */}
      <section className="py-20 bg-[#FAF7F2]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 space-y-6">
          <div className="space-y-4">
            <h2 className="font-serif text-3xl sm:text-4xl font-normal text-[#2E2A27]">
              {pageData?.sektion_2_overskrift || 'Måske mærker du...'}
            </h2>
            <div className="w-16 h-0.5 bg-[#98866C]/60" />
          </div>

          {pageData?.sektion_2_tekst ? (
            <div
              className="space-y-6 text-base sm:text-lg text-[#5C524A] leading-relaxed font-sans cms-richtext"
              dangerouslySetInnerHTML={{ __html: pageData.sektion_2_tekst }}
            />
          ) : (
            <div className="space-y-6 text-base sm:text-lg text-[#5C524A] leading-relaxed font-sans">
              <p>
                Måske er du et sted i livet, hvor du ikke længere kan eller vil fortsætte med at tilpasse dig.
              </p>
              <p>
                Måske har du været den, der altid har haft styr på det hele, været der for alle andre og glemte dig selv.
              </p>
              <p>
                Måske mærker du en uro i kroppen eller i sindet. En fornemmelse af, at noget skal slippes, men du ved ikke helt hvad.
              </p>
              <p>
                Måske er du midt i en forandring og længes efter at finde din retning.
              </p>
              <p>
                Eller måske længes du ganske enkelt efter et sted, hvor du kan sænke skuldrene, trække vejret og bare være dig.
              </p>

              <div className="py-4 space-y-1.5 text-[#2E2A27] font-medium text-lg">
                <p>Uden at skulle præstere.</p>
                <p>Uden at skulle være dygtig.</p>
                <p>Uden at skulle have svarene.</p>
                <p className="text-xl font-serif text-[#98866C] pt-2">Bare være dig.</p>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Sektion 3: Et rum, hvor du ikke skal være noget bestemt */}
      <section className="py-20 bg-white border-y border-[#E8DFD5]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 space-y-8">
          <div className="space-y-4">
            <h2 className="font-serif text-3xl sm:text-4xl font-normal text-[#2E2A27]">
              {pageData?.sektion_3_overskrift || 'Et rum, hvor du ikke skal være noget bestemt'}
            </h2>
            <div className="w-16 h-0.5 bg-[#98866C]/60" />
          </div>

          {pageData?.sektion_3_tekst ? (
            <div
              className="space-y-6 text-base sm:text-lg text-[#5C524A] leading-relaxed font-sans cms-richtext"
              dangerouslySetInnerHTML={{ __html: pageData.sektion_3_tekst }}
            />
          ) : (
            <div className="space-y-6 text-base sm:text-lg text-[#5C524A] leading-relaxed font-sans">
              <p>
                Jeg ønsker at skabe et rum, hvor vi kan mødes som kvinder – i øjenhøjde, med åben hjerte og nysgerrighed.
              </p>
              <p>
                Et rum, hvor der er plads til både det smukke, det svære, det sårbare og det, vi endnu ikke helt forstår.
              </p>
              <p>
                Her skal vi ikke fikse hinanden.
              </p>
              <p>
                Vi skal ikke skynde os videre.
              </p>
              <p>
                Vi skal øve os i at være med det, der er – og mærke det, der opstår, når vi giver os selv lov til at stoppe op og lytte.
              </p>
              <p>
                For mig handler det om at komme tættere på det sted i os, hvor vi allerede ved mere, end vi tror.
              </p>
              <div className="py-2 space-y-1.5 text-[#2E2A27] font-medium">
                <p>Det sted, hvor hjertet taler.</p>
                <p>Hvor kroppen kan få lov til at fortælle.</p>
                <p>Og hvor vi langsomt kan begynde at høre vores egen indre stemme igen.</p>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Sektion 4: En rejse hjem til dig selv - De 5 elementer */}
      <section className="py-20 bg-[#FAF7F2]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 space-y-10">
          <div className="space-y-4">
            <h2 className="font-serif text-3xl sm:text-4xl font-normal text-[#2E2A27]">
              {pageData?.sektion_4_overskrift || 'En rejse hjem til dig selv'}
            </h2>
            <p className="text-base sm:text-lg text-[#5C524A] leading-relaxed font-sans">
              {pageData?.sektion_4_undertitel || 'Forbundet fra Hjertet er en lille, intim gruppe for kvinder, der længes efter mere nærvær, indre ro og forbindelse til sig selv.'}
            </p>
            <p className="text-base sm:text-lg text-[#2E2A27] font-medium font-sans">
              Gennem fem møder inviterer jeg dig med på en indre rejse, hvor vi blandt andet skal arbejde med:
            </p>
          </div>

          <div className="space-y-6">
            <div className="bg-white p-6 sm:p-8 rounded-sm border border-[#D8CDBF] shadow-xs space-y-3">
              <h3 className="font-serif text-xl sm:text-2xl font-normal text-[#2E2A27] flex items-center gap-2">
                <span>{pageData?.kort_1_titel ? '' : '🌿'}</span>
                <span>{pageData?.kort_1_titel || '🌿 At komme ned i kroppen'}</span>
              </h3>
              <p className="text-base text-[#5C524A] leading-relaxed font-sans">
                {pageData?.kort_1_tekst || 'Vi bruger åndedræt, meditation og kropslige øvelser til at lande i kroppen og skabe mere ro og nærvær.'}
              </p>
            </div>

            <div className="bg-white p-6 sm:p-8 rounded-sm border border-[#D8CDBF] shadow-xs space-y-3">
              <h3 className="font-serif text-xl sm:text-2xl font-normal text-[#2E2A27] flex items-center gap-2">
                <span>{pageData?.kort_2_titel ? '' : '✨'}</span>
                <span>{pageData?.kort_2_titel || '✨ Astrologien og energien lige nu'}</span>
              </h3>
              <p className="text-base text-[#5C524A] leading-relaxed font-sans">
                {pageData?.kort_2_tekst || 'Vi ser på astrologien som et spejl på de temaer og bevægelser, der kan være aktive i vores liv, og undersøger, hvordan du kan bruge denne forståelse i din egen proces.'}
              </p>
            </div>

            <div className="bg-white p-6 sm:p-8 rounded-sm border border-[#D8CDBF] shadow-xs space-y-3">
              <h3 className="font-serif text-xl sm:text-2xl font-normal text-[#2E2A27] flex items-center gap-2">
                <span>{pageData?.kort_3_titel ? '' : '🌸'}</span>
                <span>{pageData?.kort_3_titel || '🌸 At lytte til kroppens sprog'}</span>
              </h3>
              <p className="text-base text-[#5C524A] leading-relaxed font-sans">
                {pageData?.kort_3_tekst || 'Vi undersøger kroppens signaler med nysgerrighed og omsorg og ser på mulige sammenhænge mellem krop, følelser og livets oplevelser med inspiration fra GNM/Germanische Heilkunde.'}
              </p>
            </div>

            <div className="bg-white p-6 sm:p-8 rounded-sm border border-[#D8CDBF] shadow-xs space-y-3">
              <h3 className="font-serif text-xl sm:text-2xl font-normal text-[#2E2A27] flex items-center gap-2">
                <span>{pageData?.kort_4_titel ? '' : '❤️'}</span>
                <span>{pageData?.kort_4_titel || '❤️ At lytte til hjertet'}</span>
              </h3>
              <p className="text-base text-[#5C524A] leading-relaxed font-sans">
                {pageData?.kort_4_tekst || 'Gennem intuition, skriveøvelser og meditation skaber vi plads til at høre din egen indre stemme og mærke, hvad der føles rigtigt for dig.'}
              </p>
            </div>

            <div className="bg-white p-6 sm:p-8 rounded-sm border border-[#D8CDBF] shadow-xs space-y-3">
              <h3 className="font-serif text-xl sm:text-2xl font-normal text-[#2E2A27] flex items-center gap-2">
                <span>{pageData?.kort_5_titel ? '' : '🕊️'}</span>
                <span>{pageData?.kort_5_titel || '🕊️ At komme hjem til dig selv'}</span>
              </h3>
              <p className="text-base text-[#5C524A] leading-relaxed font-sans">
                {pageData?.kort_5_tekst || 'Vi arbejder med nærvær via vejrtræknings øvelser, meditation og healing, derved skaber du en kærlig og respektfuld forbindelse til dig selv, din krop, dit hjerte og det, der føles sandt for dig.'}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Sektion 5: DU er ikke alene */}
      <section className="py-20 bg-white border-y border-[#E8DFD5]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 space-y-6">
          <h2 className="font-serif text-3xl sm:text-4xl font-normal text-[#2E2A27]">
            {pageData?.sektion_5_overskrift || 'DU er ikke alene'}
          </h2>

          {pageData?.sektion_5_tekst ? (
            <div
              className="space-y-6 text-base sm:text-lg text-[#5C524A] leading-relaxed font-sans cms-richtext"
              dangerouslySetInnerHTML={{ __html: pageData.sektion_5_tekst }}
            />
          ) : (
            <div className="space-y-6 text-base sm:text-lg text-[#5C524A] leading-relaxed font-sans">
              <p>
                Noget af det, jeg selv har oplevet som særligt helende og transformerende, er at opdage, at jeg ikke var alene om det, jeg gik igennem.
              </p>
              <p>
                Når vi tør dele det, der er sårbart og det kommer fra hjertet, kan vi pludselig genkende os selv i hinanden.
              </p>
              <p>
                Måske opdager du, at det, du troede kun var dit, også findes i andre.
              </p>
              <p>
                Måske bliver du spejlet på en ny måde.
              </p>
              <p>
                Og måske mærker du, at du ikke behøver at gå hele vejen alene.
              </p>
              <p>
                Det er noget af det, jeg ønsker at skabe med Forbundet fra Hjertet.
              </p>
              <p className="font-medium text-[#2E2A27]">
                Et lille fællesskab, hvor vi kan være ærlige, nysgerrige og menneskelige sammen.
              </p>
            </div>
          )}
        </div>
      </section>

      {/* Sektion 6: Praktisk */}
      <section className="py-20 bg-[#FAF7F2]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white p-8 sm:p-12 rounded-sm border border-[#D8CDBF] shadow-xs space-y-8">
            <h2 className="font-serif text-3xl sm:text-4xl font-normal text-[#2E2A27]">
              {rammerOverskrift}
            </h2>

            <div className="space-y-5 text-base sm:text-lg text-[#5C524A] leading-relaxed font-sans">
              {rammerPunkter ? (
                <div className="space-y-3">
                  {rammerPunkter.split('•').map((punkt, pIdx) => {
                    const clean = punkt.trim();
                    if (!clean) return null;
                    return (
                      <p key={pIdx} className="flex items-start gap-2.5">
                        <i className="fa-solid fa-circle-check text-[#98866C] text-sm mt-1 shrink-0" aria-hidden="true" />
                        <span>{clean}</span>
                      </p>
                    );
                  })}
                </div>
              ) : (
                <>
                  <p>
                    Jeg inviterer 6 kvinder ind i en intim og lukket lille gruppe i min klinik.
                  </p>
                  <p>
                    Vi mødes 5 gange – hver anden uge i 2,5 timer.
                  </p>
                  <p>
                    Jeg bringer mine erfaringer med mig fra Kropsterapi, Reiki healing, Coaching, Astrologi, GNM / Germanische heilkunde, samt TMC / traditionel kinesisk medicin og lader det, sammen med min intuition, danne rammen om en levende og nærværende rejse.
                  </p>
                  <div className="space-y-1.5 text-[#2E2A27] font-medium pt-2">
                    <p>Der er ikke en bestemt måde, du skal være på.</p>
                    <p>Du behøver ikke komme med et bestemt mål.</p>
                    <p>Du skal ikke præstere noget.</p>
                    <p className="text-[#98866C]">Du skal bare komme, præcis som du er.</p>
                  </div>
                </>
              )}

              <div className="p-6 bg-[#FAF7F2] rounded-sm border border-[#E8DFD5] space-y-2 mt-6">
                <p className="text-xl font-serif font-medium text-[#2E2A27]">
                  Pris: {prisTekst}
                </p>
                <p className="text-sm text-[#5C524A]">
                  Mulighed for ratebetaling.
                </p>
                <p className="text-xs text-[#98866C] uppercase tracking-wider font-semibold pt-1">
                  Pladserne er begrænset til 6 kvinder, fordi jeg ønsker at bevare det nære, trygge og fortrolige rum.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Sektion 7: Afslutning & CTA */}
      <section className="py-20 bg-white border-t border-[#E8DFD5]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 space-y-8">
          {!pageData?.indhold_html && (
            <>
              <div className="space-y-4">
                <h2 className="font-serif text-3xl sm:text-4xl font-normal text-[#2E2A27]">
                  Måske er det nu, du skal vælge dig selv
                </h2>
                <div className="w-16 h-0.5 bg-[#98866C]/60" />
              </div>

              <div className="space-y-6 text-base sm:text-lg text-[#5C524A] leading-relaxed font-sans">
                <p>
                  Hvis du mærker en stille længsel, en nysgerrighed eller noget i dig, der siger “måske er det her noget for mig”, så lyt til det.
                </p>
                <p>
                  Du behøver ikke vide præcis hvorfor.
                </p>
                <p>
                  Du behøver ikke have alle svarene.
                </p>
                <p>
                  Måske er det bare dit hjerte, der banker lidt højere.
                </p>
                <p className="font-medium text-[#2E2A27]">
                  Jeg vil elske at møde dig i det.
                </p>
                <p>
                  Send mig en personlig besked, så tager vi en stille og uforpligtende snak om, hvorvidt - Forbundet fra Hjertet - føles som den rigtige rejse for dig.
                </p>
                <p>
                  Jeg glæder mig til at åbne døren til dette rum og til at møde dig dér, hvor du er.
                </p>
                <div className="pt-4 font-serif text-xl text-[#2E2A27]">
                  <p>Med kærlighed og åbent hjerte</p>
                  <p className="italic text-[#98866C]">Lenea</p>
                </div>
              </div>
            </>
          )}

          <div className="pt-2">
            <Link
              to="/kontakt"
              className="inline-flex items-center gap-2 px-8 py-4 bg-[#98866C] text-white font-medium text-xs uppercase tracking-widest hover:bg-[#83735C] transition-colors cursor-pointer"
            >
              <span>{ctaTekst}</span>
              <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
